import { ButtonVariant } from "@repo/ui/Buttons/button";
import { iconButton } from "@repo/ui/Buttons/iconButton";
import { IconThumbDown, IconThumbUp } from "@tabler/icons-react";
import { type FC, useContext } from "react";
import { ToggleButton, ToggleGroupStateContext } from "react-aria-components";
import { tv } from "tailwind-variants";
import { SimpleTooltip } from "../tooltip/simpleTooltip.js";
import { TooltipTrigger } from "../tooltip/tooltipTrigger.js";

export const sentimentTypes = ["positive", "negative"] as const;
export type SentimentType = (typeof sentimentTypes)[number];

export function checkIsSentimentType(value: unknown): value is SentimentType {
  return sentimentTypes.includes(value as SentimentType);
}

const sentimentComponents = {
  positive: {
    Icon: IconThumbUp,
  },
  negative: {
    Icon: IconThumbDown,
  },
} as const;

const sentimentIcon = tv({
  base: "size-4",
  variants: {
    isGroupSelectionEmpty: { true: "" },
    sentimentType: { positive: "", negative: "" },
    isSelected: { true: "" },
  },
  compoundVariants: [
    {
      isGroupSelectionEmpty: false,
      isSelected: false,
      className: "opacity-30",
    },
    {
      isSelected: true,
      sentimentType: "positive",
      className: "text-green-500",
    },
    {
      isSelected: true,
      sentimentType: "negative",
      className: "text-red-500",
    },
  ],
});

// TODO: reconcile with apps/admin/src/features/insights/assessmentFeedback/sentimentToggleButton.tsx
export const SentimentToggleButton: FC<{
  sentimentType: SentimentType;
  tooltipContent?: string;
}> = ({ sentimentType, tooltipContent }) => {
  const groupState = useContext(ToggleGroupStateContext);
  const isGroupSelectionEmpty = groupState?.selectedKeys.size === 0;

  const Icon = sentimentComponents[sentimentType].Icon;

  return (
    <TooltipTrigger>
      <ToggleButton
        id={sentimentType}
        className={iconButton({
          variant: ButtonVariant.Tertiary,
          className: "overflow-hidden pressed:bg-slate-200",
        })}
        aria-label={
          sentimentType === "positive"
            ? "Positive sentiment"
            : "Negative sentiment"
        }
      >
        {({ isSelected }) => (
          <Icon
            className={sentimentIcon({
              isGroupSelectionEmpty,
              sentimentType,
              isSelected,
            })}
          />
        )}
      </ToggleButton>

      {tooltipContent ? <SimpleTooltip>{tooltipContent}</SimpleTooltip> : null}
    </TooltipTrigger>
  );
};
